import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiProblem } from '@/lib/api';
import { qk } from '@/lib/keys';
import {
  Card,
  ConfirmDialog,
  ErrorState,
  Skeleton,
  StepUpGate,
  TextInput,
  Button,
  useToast,
} from '@/components';

interface AlertRouting {
  slack_channel: string;
  slack_severities: string[];
  sms_number: string;
  sms_conditions: string[];
  email_digest_to: string;
  email_digest_hour: number;
  quiet_hours_start: string;
  quiet_hours_end: string;
}

interface AlertsResponse {
  routing: AlertRouting;
  rules: { code: string; severity: string; slack_mirror: boolean }[];
}

const E164 = /^\+[1-9]\d{7,14}$/;

/**
 * Settings > Alerts. Where an inbox item goes and who it wakes.
 *
 * The number field validates E.164 before it will save, because the live failure this
 * replaces was twelve of twenty five SMS dispatches lost to a number written as
 * 1-2487818100. An alert that fails to deliver is itself an inbox item, which is the rule
 * that catches the next one.
 */
export function Alerts() {
  const client = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<AlertRouting | null>(null);
  const [confirming, setConfirming] = useState(false);

  const query = useQuery({
    queryKey: qk.settings('alerts'),
    queryFn: () => api.get<AlertsResponse>('/settings/alerts'),
  });

  useEffect(() => {
    if (query.data && draft === null) setDraft(query.data.routing);
  }, [draft, query.data]);

  const save = useMutation({
    mutationFn: (routing: AlertRouting) =>
      api.put<{ routing: AlertRouting }>('/settings/alerts', {
        routing,
        // PUT /settings/alerts declares typedConfirm as 'ALERTS' and compares it literally.
        typed_confirm: 'ALERTS',
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.settings('alerts') });
      toast.push({ tone: 'ok', message: 'Alert routing saved and audited.' });
    },
    onError: (error) =>
      toast.push({
        tone: 'crit',
        message: error instanceof ApiProblem ? error.message : 'Routing was not saved.',
        requestId: error instanceof ApiProblem ? error.shortRequestId : null,
      }),
  });

  if (query.isLoading || !draft) {
    return (
      <Card title="Alert routing">
        {query.isError ? (
          <ErrorState message="Alert routing did not load." onRetry={() => void query.refetch()} />
        ) : (
          <Skeleton shape="card" lines={3} label="Loading alert routing" />
        )}
      </Card>
    );
  }

  const smsError = draft.sms_number && !E164.test(draft.sms_number)
    ? 'Must be E.164, for example +12485550123. A number in any other shape is dropped by the carrier without an error.'
    : null;

  return (
    <>
      <Card title="Alert routing" id="alerts">
        <StepUpGate tier={3} action="Changing where alerts go">
          <TextInput
            label="Slack channel"
            value={draft.slack_channel}
            onChange={(event) => setDraft({ ...draft, slack_channel: event.target.value })}
            hint="Critical inbox items, every tier 2 and tier 3 action, and every security event. Context lines carry a first name and a last initial, never an email, phone or address."
          />
          <TextInput
            label="SMS number"
            value={draft.sms_number}
            mono
            error={smsError}
            onChange={(event) => setDraft({ ...draft, sms_number: event.target.value })}
            hint="Site down, checkout failing, payment matcher stale beyond ten minutes."
          />
          <TextInput
            label="Daily digest recipient"
            type="email"
            value={draft.email_digest_to}
            onChange={(event) => setDraft({ ...draft, email_digest_to: event.target.value })}
          />
          <div className="o-row">
            <TextInput
              label="Quiet hours start"
              type="time"
              value={draft.quiet_hours_start}
              onChange={(event) => setDraft({ ...draft, quiet_hours_start: event.target.value })}
            />
            <TextInput
              label="Quiet hours end"
              type="time"
              value={draft.quiet_hours_end}
              onChange={(event) => setDraft({ ...draft, quiet_hours_end: event.target.value })}
            />
          </div>
          <p className="o-muted">
            Quiet hours apply to customer SMS. They never suppress a critical operational
            alert to the owner.
          </p>
          <Button
            variant="primary"
            disabled={smsError !== null}
            onClick={() => setConfirming(true)}
          >
            Save routing
          </Button>
        </StepUpGate>
      </Card>

      <Card title="Rules that mirror to Slack">
        {(query.data?.rules ?? []).map((rule) => (
          <div className="o-kv" key={rule.code}>
            <span className="o-kv__key">{rule.code}</span>
            <span className="o-kv__value">
              {rule.severity}
              {rule.slack_mirror ? ' · mirrored' : ' · in app only'}
            </span>
          </div>
        ))}
        <p className="o-muted">
          Severity and cooldown live in the inbox rules table, so they can be retuned without
          a deploy.
        </p>
      </Card>

      <ConfirmDialog
        open={confirming}
        title="Save alert routing?"
        body="This changes where operational alerts go. If it is wrong, the next outage is silent."
        effects={[
          'Slack channel and severity mapping updated',
          'SMS destination updated',
          'A failed dispatch still raises an inbox item',
        ]}
        confirmLabel="Save routing"
        typedConfirm="ALERTS"
        reasonRequired
        onCancel={() => setConfirming(false)}
        onConfirm={async () => {
          setConfirming(false);
          await save.mutateAsync(draft);
        }}
      />
    </>
  );
}
